import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  Progress,
  Button,
} from "reactstrap";
import Select from "react-select";
import cogoToast from "cogo-toast";
import { getProjects, getComponents, genericPost } from "../../services/Api";
import { ENDPOINTS, SUCCESS_RESULT } from "../../Constants";
import LocationPickerModal from "../../components/LocationPickerModal";

const normalizePhoneDigits = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length >= 9) return digits.slice(-9);
  return digits;
};

const parsePhoneListText = (text) => {
  const numbers = new Set();
  String(text || "")
    .split(/[\n\r\t,;]+/)
    .map((part) => part.trim().replace(/^["']|["']$/g, ""))
    .filter(Boolean)
    .forEach((part) => {
      let value = part;
      if (/^[\d.]+[eE][+\-]?\d+$/.test(part)) {
        const numeric = Number(part);
        if (!Number.isNaN(numeric)) value = String(Math.trunc(numeric));
      } else if (/^\d+\.0+$/.test(part)) {
        value = part.split(".")[0];
      }
      const normalized = normalizePhoneDigits(value);
      if (normalized) numbers.add(normalized);
    });
  return [...numbers];
};

const getBeneficiaryTelephone = (beneficiary) =>
  beneficiary?.telephone ||
  beneficiary?.beneficiary_details?.telephone ||
  beneficiary?.phone ||
  beneficiary?.mobile ||
  "";

const getLocationLabel = (beneficiary) => {
  const details = beneficiary?.beneficiary_details || {};
  const camp = details?.camp_name || beneficiary?.camp || "";
  const village =
    details?.village_name || beneficiary?.village || details?.village || "";
  const district =
    details?.district_name || beneficiary?.district || details?.district || "";
  return [camp, village, district].filter(Boolean).join(", ") || "-";
};

const getBeneficiarySearchHaystack = (beneficiary) => {
  const details = beneficiary?.beneficiary_details || {};
  return [
    beneficiary?.name,
    getBeneficiaryTelephone(beneficiary),
    beneficiary?.beneficiarymanagement_id,
    getLocationLabel(beneficiary),
    details?.camp_name,
    details?.village_name,
    details?.district_name,
    details?.region_name,
    details?.zone_name,
    details?.country_name,
    beneficiary?.camp,
    beneficiary?.village,
    beneficiary?.district,
  ]
    .map((v) => String(v || "").toLowerCase())
    .join(" ");
};

const beneficiaryMatchesSearch = (beneficiary, searchTerm) => {
  const terms = String(searchTerm || "")
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  if (terms.length === 0) return true;
  const haystack = getBeneficiarySearchHaystack(beneficiary);
  return terms.some((term) => haystack.includes(term));
};

const getPhoneMatchVariants = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return [];
  const variants = new Set([digits]);
  if (digits.length >= 9) variants.add(digits.slice(-9));
  return [...variants];
};

const beneficiaryMatchesPhoneList = (beneficiary, phoneFilterSet) => {
  if (!phoneFilterSet || phoneFilterSet.size === 0) return true;
  const variants = getPhoneMatchVariants(getBeneficiaryTelephone(beneficiary));
  if (variants.length === 0) return false;
  for (const variant of variants) {
    if (phoneFilterSet.has(variant)) return true;
    for (const filterPhone of phoneFilterSet) {
      if (variant.endsWith(filterPhone) || filterPhone.endsWith(variant)) return true;
    }
  }
  return false;
};

const CloneBeneficiaries = () => {
  const [step, setStep] = useState(1);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingSourceComponents, setIsLoadingSourceComponents] = useState(false);
  const [isLoadingBeneficiaries, setIsLoadingBeneficiaries] = useState(false);
  const [isLoadingTargetComponents, setIsLoadingTargetComponents] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [cloneConfirmOpen, setCloneConfirmOpen] = useState(false);
  /** @type {null | { message: string, status: number, data: object, labels: object, beneficiaryNames: Record<string, string> }} */
  const [cloneReport, setCloneReport] = useState(null);

  const [projects, setProjects] = useState([]);
  const [sourceComponents, setSourceComponents] = useState([]);
  const [targetComponents, setTargetComponents] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [beneficiariesTotal, setBeneficiariesTotal] = useState(0);
  const [selectedBeneficiaries, setSelectedBeneficiaries] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [phoneListFilter, setPhoneListFilter] = useState([]);
  const [phoneListInput, setPhoneListInput] = useState("");
  const [phoneListModalOpen, setPhoneListModalOpen] = useState(false);

  const [sourceProjectId, setSourceProjectId] = useState("");
  const [sourceComponentId, setSourceComponentId] = useState("");
  const [targetProjectId, setTargetProjectId] = useState("");
  const [targetComponentId, setTargetComponentId] = useState("");
  const [selectedSourceLocation, setSelectedSourceLocation] = useState(null);
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 46,
      fontSize: "1.05rem",
      borderRadius: 8,
      borderColor: state.isFocused ? "#86b7fe" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(13, 110, 253, 0.25)" : "0 1px 3px rgba(0,0,0,0.06)",
      "&:hover": {
        borderColor: "#86b7fe",
      },
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      fontSize: "1.05rem",
    }),
    singleValue: (base) => ({ ...base, fontSize: "1.05rem" }),
    input: (base) => ({ ...base, fontSize: "1.05rem" }),
    option: (base) => ({ ...base, fontSize: "1.05rem", padding: "10px 12px" }),
    placeholder: (base) => ({
      ...base,
      color: "#6c757d",
      fontSize: "1.05rem",
    }),
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = () => {
    setIsLoadingProjects(true);
    getProjects()
      .then((res) => {
        setIsLoadingProjects(false);
        if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
          setProjects(res.data.data || []);
          return;
        }
        cogoToast.error(res?.data?.message || "Failed to load projects", { hideAfter: 4 });
      })
      .catch(() => {
        setIsLoadingProjects(false);
        cogoToast.error("Failed to load projects", { hideAfter: 4 });
      });
  };

  const loadComponents = (projectId, type) => {
    if (!projectId) return;
    if (type === "source") {
      setIsLoadingSourceComponents(true);
      setSourceComponents([]);
      setSourceComponentId("");
      setSelectedSourceLocation(null);
      setBeneficiaries([]);
      setBeneficiariesTotal(0);
      setSelectedBeneficiaries([]);
      setPhoneListFilter([]);
      setPhoneListInput("");
    } else {
      setIsLoadingTargetComponents(true);
      setTargetComponents([]);
      setTargetComponentId("");
    }

    getComponents({ project_id: projectId })
      .then((res) => {
        if (type === "source") setIsLoadingSourceComponents(false);
        if (type === "target") setIsLoadingTargetComponents(false);

        if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
          const list = res.data.data || [];
          if (type === "source") setSourceComponents(list);
          if (type === "target") setTargetComponents(list);
          return;
        }
        cogoToast.error(res?.data?.message || "Failed to load components", { hideAfter: 4 });
      })
      .catch(() => {
        if (type === "source") setIsLoadingSourceComponents(false);
        if (type === "target") setIsLoadingTargetComponents(false);
        cogoToast.error("Failed to load components", { hideAfter: 4 });
      });
  };

  const loadBeneficiaries = () => {
    if (!sourceProjectId || !sourceComponentId) {
      cogoToast.error("Select source project and component first", { hideAfter: 3 });
      return;
    }
    setIsLoadingBeneficiaries(true);
    setBeneficiaries([]);
    setBeneficiariesTotal(0);
    setSelectedBeneficiaries([]);
    setPhoneListFilter([]);
    setPhoneListInput("");

    const payload = {
      project_id: sourceProjectId,
      component_id: sourceComponentId,
      page: 1,
      per_page: 10000,
    };
    if (selectedSourceLocation?.camp) {
      payload.camp_id = Number(selectedSourceLocation.camp);
    }

    genericPost(ENDPOINTS.LIST_ACTIVE_BENEFICIARIES_DETAILS, payload)
      .then((res) => {
        setIsLoadingBeneficiaries(false);
        if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
          const responseData = res?.data?.data;
          const list = Array.isArray(responseData)
            ? responseData
            : Array.isArray(responseData?.beneficiaries)
            ? responseData.beneficiaries
            : [];
          const total =
            responseData?.pagination?.total ??
            responseData?.total ??
            res?.data?.pagination?.total ??
            list.length;
          setBeneficiaries(list);
          setBeneficiariesTotal(Number(total) || list.length);
          cogoToast.success(`Loaded ${list.length} beneficiary(ies)`, { hideAfter: 3 });
          return;
        }
        cogoToast.error(res?.data?.message || "Failed to load beneficiaries", { hideAfter: 4 });
      })
      .catch(() => {
        setIsLoadingBeneficiaries(false);
        cogoToast.error("Failed to load beneficiaries", { hideAfter: 4 });
      });
  };

  const filteredBeneficiaries = useMemo(() => {
    let list = beneficiaries;

    if (phoneListFilter.length > 0) {
      const phoneFilterSet = new Set(phoneListFilter);
      list = list.filter((b) => beneficiaryMatchesPhoneList(b, phoneFilterSet));
    }

    list = list.filter((b) => beneficiaryMatchesSearch(b, searchTerm));

    const getSortValue = (b) => {
      if (sortField === "telephone") return getBeneficiaryTelephone(b) || "";
      if (sortField === "location") {
        const label = getLocationLabel(b);
        return label === "-" ? "" : label;
      }
      return b?.name || "";
    };

    return [...list].sort((a, b) => {
      const x = String(getSortValue(a)).toLowerCase();
      const y = String(getSortValue(b)).toLowerCase();
      if (x < y) return sortDirection === "asc" ? -1 : 1;
      if (x > y) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [beneficiaries, searchTerm, phoneListFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    setSortDirection("asc");
  };

  const sortMark = (field) =>
    sortField === field ? (sortDirection === "asc" ? " ▲" : " ▼") : "";

  const applyPhoneListFilter = () => {
    const parsed = parsePhoneListText(phoneListInput);
    if (parsed.length === 0) {
      cogoToast.error("Paste at least one phone number", { hideAfter: 3 });
      return;
    }
    const phoneFilterSet = new Set(parsed);
    const matchCount = beneficiaries.filter((b) => beneficiaryMatchesPhoneList(b, phoneFilterSet)).length;
    setPhoneListFilter(parsed);
    setPhoneListModalOpen(false);
    cogoToast.success(
      `Showing ${matchCount.toLocaleString()} beneficiary(ies) matching ${parsed.length.toLocaleString()} phone number(s)`,
      { hideAfter: 4 }
    );
  };

  const clearPhoneListFilter = () => {
    setPhoneListFilter([]);
    setPhoneListInput("");
    cogoToast.info("Phone list filter cleared", { hideAfter: 3 });
  };

  const projectOptions = useMemo(
    () =>
      [...projects]
        .sort((a, b) => (a?.project_name || "").localeCompare(b?.project_name || ""))
        .map((p) => ({ value: p.id, label: p.project_name || "Unnamed Project" })),
    [projects]
  );

  const sourceComponentOptions = useMemo(
    () =>
      [...sourceComponents]
        .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
        .map((c) => ({ value: c.id, label: c.name || "Unnamed Component" })),
    [sourceComponents]
  );

  const targetComponentOptions = useMemo(
    () =>
      [...targetComponents]
        .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
        .map((c) => ({ value: c.id, label: c.name || "Unnamed Component" })),
    [targetComponents]
  );

  const getBeneficiaryManagementId = (b) => b?.beneficiarymanagement_id || null;

  const isSelected = (beneficiarymanagementId) =>
    !!beneficiarymanagementId && selectedBeneficiaries.includes(beneficiarymanagementId);

  const toggleBeneficiary = (beneficiarymanagementId) => {
    if (!beneficiarymanagementId) return;
    setSelectedBeneficiaries((prev) =>
      prev.includes(beneficiarymanagementId)
        ? prev.filter((x) => x !== beneficiarymanagementId)
        : [...prev, beneficiarymanagementId]
    );
  };

  const selectableFilteredBeneficiaries = useMemo(
    () => filteredBeneficiaries.filter((b) => !!getBeneficiaryManagementId(b)),
    [filteredBeneficiaries]
  );

  const selectedInTableCount = useMemo(() => {
    const selectedSet = new Set(selectedBeneficiaries);
    return selectableFilteredBeneficiaries.filter((b) =>
      selectedSet.has(getBeneficiaryManagementId(b))
    ).length;
  }, [selectableFilteredBeneficiaries, selectedBeneficiaries]);

  const toggleSelectAll = () => {
    const visibleMgmtIds = selectableFilteredBeneficiaries.map((b) => getBeneficiaryManagementId(b));
    const allVisibleSelected =
      visibleMgmtIds.length > 0 && visibleMgmtIds.every((id) => selectedBeneficiaries.includes(id));
    if (allVisibleSelected) {
      setSelectedBeneficiaries((prev) => prev.filter((id) => !visibleMgmtIds.includes(id)));
      return;
    }
    setSelectedBeneficiaries(visibleMgmtIds);
  };

  const handleNext = () => {
    const phoneFilterSet =
      phoneListFilter.length > 0 ? new Set(phoneListFilter) : null;
    const ids = beneficiaries
      .filter((b) => {
        const id = getBeneficiaryManagementId(b);
        if (!id || !selectedBeneficiaries.includes(id)) return false;
        if (phoneFilterSet) return beneficiaryMatchesPhoneList(b, phoneFilterSet);
        return true;
      })
      .map((b) => getBeneficiaryManagementId(b));

    if (ids.length === 0) {
      cogoToast.error("Select at least one beneficiary", { hideAfter: 3 });
      return;
    }
    setSelectedBeneficiaries(ids);
    setStep(2);
  };

  const openCloneConfirm = () => {
    if (!targetProjectId || !targetComponentId) {
      cogoToast.error("Select destination project and component", { hideAfter: 3 });
      return;
    }
    if (selectedBeneficiaries.length === 0) {
      cogoToast.error("No beneficiaries selected", { hideAfter: 3 });
      return;
    }
    setCloneConfirmOpen(true);
  };

  const executeClone = () => {
    if (!targetProjectId || !targetComponentId || selectedBeneficiaries.length === 0) {
      return;
    }

    const labels = {
      sourceProject: projectOptions.find((o) => o.value === sourceProjectId)?.label || sourceProjectId || "—",
      sourceComponent: sourceComponentOptions.find((o) => o.value === sourceComponentId)?.label || sourceComponentId || "—",
      targetProject: projectOptions.find((o) => o.value === targetProjectId)?.label || targetProjectId || "—",
      targetComponent: targetComponentOptions.find((o) => o.value === targetComponentId)?.label || targetComponentId || "—",
    };

    const beneficiaryNames = {};
    beneficiaries.forEach((b) => {
      const mid = getBeneficiaryManagementId(b);
      if (mid) beneficiaryNames[mid] = b.name || "—";
      if (b?.id) beneficiaryNames[b.id] = b.name || "—";
    });

    setIsCloning(true);
    genericPost(ENDPOINTS.CLONE_BENEFICIARIES, {
      source_project_id: sourceProjectId,
      source_component_id: sourceComponentId,
      target_project_id: targetProjectId,
      target_component_id: targetComponentId,
      beneficiary_ids: selectedBeneficiaries.filter(Boolean),
    })
      .then((res) => {
        const payload = res?.data;
        if (res?.status === 200 && payload) {
          const d = payload.data;
          if (payload.status === SUCCESS_RESULT) {
            const totalCloned = typeof d?.total_cloned === "number" ? d.total_cloned : null;
            const totalFailed = typeof d?.total_failed === "number" ? d.total_failed : null;
            if (totalFailed > 0 && totalCloned === 0) {
              cogoToast.warn(payload.message || "Clone completed with errors", { hideAfter: 5 });
            } else if (totalFailed > 0) {
              cogoToast.warn(payload.message || "Some beneficiaries could not be cloned", { hideAfter: 5 });
            } else {
              cogoToast.success(payload.message || "Beneficiaries cloned successfully", { hideAfter: 4 });
            }
            if (d && typeof d === "object") {
              setCloneReport({
                message: payload.message || "",
                status: payload.status,
                data: d,
                labels,
                beneficiaryNames,
              });
            }
          } else {
            cogoToast.error(payload.message || "Clone failed", { hideAfter: 5 });
            if (d && typeof d === "object") {
              setCloneReport({
                message: payload.message || "Clone failed",
                status: payload.status,
                data: d,
                labels,
                beneficiaryNames,
              });
            }
          }
          setStep(1);
          setSourceProjectId("");
          setSourceComponentId("");
          setSourceComponents([]);
          setTargetProjectId("");
          setTargetComponentId("");
          setTargetComponents([]);
          setBeneficiaries([]);
          setSelectedBeneficiaries([]);
          setSearchTerm("");
          return;
        }
        cogoToast.error(res?.data?.message || "Clone failed", { hideAfter: 4 });
      })
      .catch(() => {
        cogoToast.error("Clone failed", { hideAfter: 4 });
      })
      .finally(() => {
        setIsCloning(false);
        setCloneConfirmOpen(false);
      });
  };

  const dismissCloneReport = () => setCloneReport(null);

  const rowStatusBadge = (status) => {
    const ok = Number(status) === SUCCESS_RESULT;
    return (
      <span
        className={`badge ${ok ? "badge-success" : "badge-danger"}`}
        style={{ fontSize: "0.9rem", padding: "0.35em 0.55em" }}
      >
        {ok ? "OK" : "Failed"}
      </span>
    );
  };

  const truncateText = (text, max = 220) => {
    if (!text || typeof text !== "string") return "";
    const t = text.trim();
    if (t.length <= max) return t;
    return `${t.slice(0, max)}…`;
  };

  const previewLabels = useMemo(
    () => ({
      sourceProject: projectOptions.find((o) => o.value === sourceProjectId)?.label || "",
      sourceComponent: sourceComponentOptions.find((o) => o.value === sourceComponentId)?.label || "",
      targetProject: projectOptions.find((o) => o.value === targetProjectId)?.label || "",
      targetComponent: targetComponentOptions.find((o) => o.value === targetComponentId)?.label || "",
    }),
    [projectOptions, sourceComponentOptions, targetComponentOptions, sourceProjectId, sourceComponentId, targetProjectId, targetComponentId]
  );

  const endpointSummaryCard = (role, accentColor, projectLabel, componentLabel, projectId, componentId, padding = "p-3") => (
    <div
      className={`border bg-white h-100 rounded ${padding}`}
      style={{
        borderLeft: `4px solid ${accentColor}`,
        boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
        fontSize: "1rem",
      }}
    >
      <div
        className="text-muted text-uppercase font-weight-bold mb-2"
        style={{ letterSpacing: "0.06em", fontSize: "0.8125rem" }}
      >
        {role}
      </div>
      <div className="font-weight-bold text-danger" style={{ fontSize: "1.2rem", lineHeight: 1.3 }}>
        {projectLabel || "—"}
      </div>
      <div className="text-secondary mt-1" style={{ fontSize: "1.05rem" }}>
        {componentLabel || "—"}
      </div>
      <div className="mt-2 font-monospace text-muted" style={{ fontSize: "0.875rem", wordBreak: "break-all" }}>
        <div>Project ID: {projectId || "—"}</div>
        <div>Component ID: {componentId || "—"}</div>
      </div>
    </div>
  );

  const allSelected =
    selectableFilteredBeneficiaries.length > 0 &&
    selectableFilteredBeneficiaries.every((b) =>
      selectedBeneficiaries.includes(getBeneficiaryManagementId(b))
    );

  const selectedBeneficiaryDetails = useMemo(() => {
    const selectedSet = new Set(selectedBeneficiaries);
    return beneficiaries.filter(
      (b) => getBeneficiaryManagementId(b) && selectedSet.has(getBeneficiaryManagementId(b))
    );
  }, [beneficiaries, selectedBeneficiaries]);

  const selectedSourceLocationLabel = useMemo(() => {
    if (!selectedSourceLocation) return "";
    return [
      selectedSourceLocation?.labels?.country,
      selectedSourceLocation?.labels?.zone,
      selectedSourceLocation?.labels?.region,
      selectedSourceLocation?.labels?.district,
      selectedSourceLocation?.labels?.village,
      selectedSourceLocation?.labels?.camp,
    ]
      .filter(Boolean)
      .join(" / ");
  }, [selectedSourceLocation]);

  const clearSourceLocation = () => {
    setSelectedSourceLocation(null);
    setBeneficiaries([]);
    setSelectedBeneficiaries([]);
  };

  const handleSourceLocationSelect = (payload) => {
    setSelectedSourceLocation(payload);
    setIsLocationPickerOpen(false);
    setBeneficiaries([]);
    setSelectedBeneficiaries([]);
  };

  const closeCloneModal = () => {
    if (!isCloning) setCloneConfirmOpen(false);
  };

  return (
    <div style={{ padding: "24px", minHeight: "100vh", backgroundColor: "#f8f9fa", fontSize: "1.0625rem" }}>
      <h2 className="font-weight-bold" style={{ marginBottom: 12, fontSize: "1.85rem" }}>
        Clone Beneficiaries
      </h2>
      <Breadcrumb className="ml-1 mb-3" style={{ fontSize: "1rem" }}>
        <BreadcrumbItem>
          <Link to="/dashboard">Dashboard</Link>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <Link to="/projects/list">Projects</Link>
        </BreadcrumbItem>
        <BreadcrumbItem active>Clone Beneficiaries</BreadcrumbItem>
      </Breadcrumb>

      <div className="card shadow-sm">
        <div className="card-body py-4 px-4 px-md-5">
          <div className="mb-4">
            <span
              className={`badge ${step === 1 ? "badge-primary" : "badge-secondary"} mr-2`}
              style={{ fontSize: "0.95rem", padding: "0.4em 0.65em" }}
            >
              Step 1
            </span>
            <span
              className={`badge ${step === 2 ? "badge-primary" : "badge-secondary"}`}
              style={{ fontSize: "0.95rem", padding: "0.4em 0.65em" }}
            >
              Step 2
            </span>
          </div>

          {step === 1 && (
            <>
              <h5 className="mb-3 font-weight-bold" style={{ fontSize: "1.35rem" }}>
                Source selection
              </h5>
              <div className="row mb-4">
                <div className="col-md-6 mb-3 mb-md-0">
                  <label className="form-label font-weight-bold" style={{ fontSize: "1.05rem" }}>
                    Source Project
                  </label>
                  <Select
                    styles={selectStyles}
                    isSearchable
                    isClearable
                    options={projectOptions}
                    value={projectOptions.find((x) => x.value === sourceProjectId) || null}
                    onChange={(option) => {
                      const projectId = option?.value || "";
                      setSourceProjectId(projectId);
                      if (!projectId) {
                        setSourceComponentId("");
                        setSourceComponents([]);
                        setSelectedSourceLocation(null);
                        setBeneficiaries([]);
                        setSelectedBeneficiaries([]);
                        return;
                      }
                      loadComponents(projectId, "source");
                    }}
                    disabled={isLoadingProjects}
                    placeholder={isLoadingProjects ? "Loading projects..." : "Select source project..."}
                    noOptionsMessage={() => "No projects found"}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label font-weight-bold" style={{ fontSize: "1.05rem" }}>
                    Source Component
                  </label>
                  <Select
                    styles={selectStyles}
                    isSearchable
                    isClearable
                    options={sourceComponentOptions}
                    value={sourceComponentOptions.find((x) => x.value === sourceComponentId) || null}
                    onChange={(option) => {
                      setSourceComponentId(option?.value || "");
                      setSelectedSourceLocation(null);
                      setBeneficiaries([]);
                      setSelectedBeneficiaries([]);
                    }}
                    isDisabled={!sourceProjectId || isLoadingSourceComponents}
                    placeholder={isLoadingSourceComponents ? "Loading components..." : "Select source component..."}
                    noOptionsMessage={() => "No components found"}
                  />
                </div>
              </div>

              {sourceProjectId && sourceComponentId && (
                <div className="row mb-4">
                  <div className="col-12 col-lg-8">
                    {endpointSummaryCard(
                      "Source",
                      "#0d6efd",
                      previewLabels.sourceProject,
                      previewLabels.sourceComponent,
                      sourceProjectId,
                      sourceComponentId
                    )}
                  </div>
                </div>
              )}

              <div className="mb-3 d-flex flex-wrap align-items-center" style={{ gap: 12 }}>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-lg"
                  onClick={() => setIsLocationPickerOpen(true)}
                  disabled={!sourceProjectId || !sourceComponentId}
                >
                  Select Location
                </button>
                {selectedSourceLocationLabel ? (
                  <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                    <span className="badge badge-info" style={{ fontSize: "0.95rem", padding: "0.5em 0.75em", whiteSpace: "normal", textAlign: "left" }}>
                      {selectedSourceLocationLabel}
                    </span>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearSourceLocation}>
                      Clear location
                    </button>
                  </div>
                ) : (
                  <span className="text-muted" style={{ fontSize: "1rem" }}>
                    Optional: filter by location (camp narrows the list).
                  </span>
                )}
              </div>

              <div className="mb-3">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={loadBeneficiaries}
                  disabled={isLoadingBeneficiaries}
                >
                  {isLoadingBeneficiaries ? "Loading..." : "Load Beneficiaries"}
                </button>
              </div>

              <div
                className="mb-3 d-flex flex-wrap align-items-center justify-content-between"
                style={{
                  gap: 12,
                  padding: "12px 14px",
                  borderRadius: 8,
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div className="d-flex flex-wrap align-items-center" style={{ gap: 16 }}>
                  <span className="badge badge-primary" style={{ fontSize: "1rem", padding: "0.5em 0.85em" }}>
                    Total returned: {beneficiariesTotal.toLocaleString()}
                  </span>
                  {phoneListFilter.length > 0 ? (
                    <span className="badge badge-info" style={{ fontSize: "1rem", padding: "0.5em 0.85em" }}>
                      Showing in table: {filteredBeneficiaries.length.toLocaleString()}
                    </span>
                  ) : (
                    <span className="badge badge-light border" style={{ fontSize: "1rem", padding: "0.5em 0.85em" }}>
                      Showing in table: {filteredBeneficiaries.length.toLocaleString()}
                    </span>
                  )}
                  <span className="badge badge-success" style={{ fontSize: "1rem", padding: "0.5em 0.85em" }}>
                    Total selected: {selectedBeneficiaries.length.toLocaleString()}
                    {phoneListFilter.length > 0 || searchTerm.trim() ? (
                      <span className="font-weight-normal">
                        {" "}
                        ({selectedInTableCount.toLocaleString()} in current table)
                      </span>
                    ) : null}
                  </span>
                  {searchTerm.trim() && (
                    <span className="text-muted" style={{ fontSize: "0.95rem" }}>
                      Showing {filteredBeneficiaries.length.toLocaleString()} matching search
                    </span>
                  )}
                  {phoneListFilter.length > 0 && (
                    <span className="badge badge-warning" style={{ fontSize: "1rem", padding: "0.5em 0.85em" }}>
                      Phone list filter: {phoneListFilter.length.toLocaleString()} numbers
                    </span>
                  )}
                </div>
                <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                  <button className="btn btn-primary btn-lg" onClick={handleNext}>
                    Next
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={toggleSelectAll}
                    disabled={selectableFilteredBeneficiaries.length === 0}
                    style={{ fontSize: "1rem" }}
                  >
                    {allSelected ? "Unselect all" : "Select all in table"}
                  </button>
                </div>
              </div>

              <div className="mb-2 d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                <input
                  className="form-control shadow-sm"
                  style={{ maxWidth: 420, fontSize: "1.05rem", padding: "0.55rem 0.85rem" }}
                  placeholder="Search name, phone, or location (comma for multi: ali,omar)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  style={{ fontSize: "1rem", whiteSpace: "nowrap" }}
                  onClick={() => setPhoneListModalOpen(true)}
                >
                  Filter with phone list
                </button>
                {phoneListFilter.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    style={{ fontSize: "1rem", whiteSpace: "nowrap" }}
                    onClick={clearPhoneListFilter}
                  >
                    Clear phone list
                  </button>
                )}
              </div>

              <div className="table-responsive" style={{ maxHeight: 1260, overflowY: "auto" }}>
                <table className="table table-bordered table-hover align-middle" style={{ fontSize: "1.02rem" }}>
                  <thead style={{ position: "sticky", top: 0, backgroundColor: "white", zIndex: 1 }}>
                    <tr>
                      <th style={{ width: 56 }} className="text-center">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          disabled={selectableFilteredBeneficiaries.length === 0}
                          onChange={toggleSelectAll}
                          title={allSelected ? "Unselect all" : "Select all"}
                          aria-label={allSelected ? "Unselect all" : "Select all"}
                        />
                      </th>
                      <th
                        role="button"
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => handleSort("name")}
                      >
                        Name{sortMark("name")}
                      </th>
                      <th
                        role="button"
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => handleSort("telephone")}
                      >
                        Telephone{sortMark("telephone")}
                      </th>
                      <th
                        role="button"
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => handleSort("location")}
                      >
                        Location{sortMark("location")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBeneficiaries.map((b) => {
                      const mid = getBeneficiaryManagementId(b);
                      return (
                      <tr key={mid || b.id}>
                        <td className="text-center">
                          <input
                            type="checkbox"
                            checked={isSelected(mid)}
                            disabled={!mid}
                            title={!mid ? "Missing beneficiary management id" : undefined}
                            onChange={() => toggleBeneficiary(mid)}
                          />
                        </td>
                        <td>{b.name || "-"}</td>
                        <td>{getBeneficiaryTelephone(b) || "-"}</td>
                        <td>{getLocationLabel(b)}</td>
                      </tr>
                    );
                    })}
                    {filteredBeneficiaries.length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-center text-muted">
                          {phoneListFilter.length > 0
                            ? "No beneficiaries match the pasted phone list"
                            : "No beneficiaries found"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

            </>
          )}

          {step === 2 && (
            <>
              <h5 className="mb-3 font-weight-bold" style={{ fontSize: "1.35rem" }}>
                Destination selection
              </h5>
              <p className="text-muted mb-2" style={{ fontSize: "1.02rem" }}>
                Review source and target before running the clone. IDs update as you change selections.
              </p>
              <div className="row mb-4">
                <div className="col-md-6 mb-3 mb-md-0">
                  {endpointSummaryCard(
                    "Source",
                    "#0d6efd",
                    previewLabels.sourceProject,
                    previewLabels.sourceComponent,
                    sourceProjectId,
                    sourceComponentId
                  )}
                </div>
                <div className="col-md-6">
                  {endpointSummaryCard(
                    "Target",
                    "#198754",
                    previewLabels.targetProject,
                    previewLabels.targetComponent,
                    targetProjectId,
                    targetComponentId
                  )}
                </div>
              </div>
              <p className="text-muted mb-3" style={{ fontSize: "1.05rem" }}>
                <span className="font-weight-bold text-dark">Selected beneficiaries:</span> {selectedBeneficiaries.length}
              </p>
              <div className="card border-0 bg-light mb-3">
                <div className="card-body p-3">
                  <h6 className="mb-2 font-weight-bold" style={{ fontSize: "1.15rem" }}>
                    Selected Beneficiaries Preview
                  </h6>
                  <div className="table-responsive" style={{ maxHeight: 250, overflowY: "auto" }}>
                    <table className="table table-striped align-middle mb-0" style={{ fontSize: "1.02rem" }}>
                      <thead>
                        <tr>
                          <th style={{ width: 50 }}>#</th>
                          <th>Name</th>
                          <th>Phone</th>
                          <th>Location</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedBeneficiaryDetails.map((b, index) => (
                          <tr key={getBeneficiaryManagementId(b) || b.id}>
                            <td>{index + 1}</td>
                            <td>{b.name || "-"}</td>
                            <td>{getBeneficiaryTelephone(b) || "-"}</td>
                            <td>{getLocationLabel(b)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-6 mb-3 mb-md-0">
                  <label className="form-label font-weight-bold" style={{ fontSize: "1.05rem" }}>
                    Destination Project
                  </label>
                  <Select
                    styles={selectStyles}
                    isSearchable
                    isClearable
                    options={projectOptions}
                    value={projectOptions.find((x) => x.value === targetProjectId) || null}
                    onChange={(option) => {
                      const projectId = option?.value || "";
                      setTargetProjectId(projectId);
                      if (!projectId) {
                        setTargetComponentId("");
                        setTargetComponents([]);
                        return;
                      }
                      loadComponents(projectId, "target");
                    }}
                    placeholder="Select destination project..."
                    noOptionsMessage={() => "No projects found"}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label font-weight-bold" style={{ fontSize: "1.05rem" }}>
                    Destination Component
                  </label>
                  <Select
                    styles={selectStyles}
                    isSearchable
                    isClearable
                    options={targetComponentOptions}
                    value={targetComponentOptions.find((x) => x.value === targetComponentId) || null}
                    onChange={(option) => setTargetComponentId(option?.value || "")}
                    isDisabled={!targetProjectId || isLoadingTargetComponents}
                    placeholder={isLoadingTargetComponents ? "Loading components..." : "Select destination component..."}
                    noOptionsMessage={() => "No components found"}
                  />
                </div>
              </div>

              <div className="d-flex flex-wrap" style={{ gap: 10 }}>
                <button className="btn btn-outline-secondary btn-lg" onClick={() => setStep(1)} disabled={isCloning}>
                  Back
                </button>
                <button className="btn btn-success btn-lg" onClick={openCloneConfirm} disabled={isCloning}>
                  Clone
                </button>
              </div>
            </>
          )}

          {cloneReport && (
            <div
              className="mt-4 border rounded shadow-sm overflow-hidden"
              style={{ background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)", fontSize: "1.05rem" }}
            >
              <div className="d-flex align-items-center justify-content-between px-3 py-3 border-bottom bg-white">
                <div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
                  <span
                    className="font-weight-bold text-secondary text-uppercase"
                    style={{ letterSpacing: "0.04em", fontSize: "0.95rem" }}
                  >
                    Clone result
                  </span>
                  {cloneReport.status === SUCCESS_RESULT ? (
                    <span className="badge badge-success" style={{ fontSize: "0.95rem", padding: "0.4em 0.65em" }}>
                      Completed
                    </span>
                  ) : (
                    <span className="badge badge-danger" style={{ fontSize: "0.95rem", padding: "0.4em 0.65em" }}>
                      Issue
                    </span>
                  )}
                </div>
                <button type="button" className="btn btn-outline-secondary" onClick={dismissCloneReport} style={{ fontSize: "1rem" }}>
                  Close
                </button>
              </div>

              <div className="p-3 p-md-4">
                {cloneReport.message && (
                  <div
                    className={`alert mb-3 py-3 px-3 mb-3 ${
                      cloneReport.status === SUCCESS_RESULT ? "alert-success border-0" : "alert-warning border-0"
                    }`}
                    role="status"
                    style={{ fontSize: "1.05rem" }}
                  >
                    {cloneReport.message}
                  </div>
                )}

                {cloneReport.data && typeof cloneReport.data === "object" && (
                  <>
                    <div className="row mb-4">
                      {[
                        { label: "Requested", value: cloneReport.data.total_requested, tone: "primary" },
                        { label: "Cloned", value: cloneReport.data.total_cloned, tone: "success" },
                        { label: "Failed", value: cloneReport.data.total_failed, tone: "danger" },
                      ].map(({ label, value, tone }) => (
                        <div key={label} className="col-12 col-sm-4 mb-2 mb-sm-0">
                          <div
                            className="p-3 h-100 border bg-white rounded"
                            style={{ borderLeft: `4px solid`, borderLeftColor: tone === "primary" ? "#007bff" : tone === "success" ? "#28a745" : "#dc3545" }}
                          >
                            <div className="text-muted" style={{ fontSize: "0.95rem" }}>
                              {label}
                            </div>
                            <div className={`h4 mb-0 font-weight-bold text-${tone}`}>{value ?? "—"}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="row mb-4">
                      <div className="col-md-6 mb-3 mb-md-0">
                        {endpointSummaryCard(
                          "Source",
                          "#0d6efd",
                          cloneReport.labels?.sourceProject,
                          cloneReport.labels?.sourceComponent,
                          cloneReport.data.source_project_id,
                          cloneReport.data.source_component_id
                        )}
                      </div>
                      <div className="col-md-6">
                        {endpointSummaryCard(
                          "Target",
                          "#198754",
                          cloneReport.labels?.targetProject,
                          cloneReport.labels?.targetComponent,
                          cloneReport.data.target_project_id,
                          cloneReport.data.target_component_id
                        )}
                      </div>
                    </div>

                    {Array.isArray(cloneReport.data.results) && cloneReport.data.results.length > 0 && (
                      <div>
                        <div className="font-weight-bold mb-2" style={{ fontSize: "1.15rem" }}>
                          Per-beneficiary results
                        </div>
                        <div className="table-responsive border rounded">
                          <table className="table table-hover align-middle mb-0" style={{ fontSize: "1rem" }}>
                            <thead className="table-light">
                              <tr>
                                <th style={{ minWidth: 140 }}>Beneficiary</th>
                                <th style={{ width: 90 }}>Status</th>
                                <th>Message</th>
                                <th style={{ minWidth: 200 }}>Details</th>
                              </tr>
                            </thead>
                            <tbody>
                              {cloneReport.data.results.map((row, idx) => {
                                const bid = row.beneficiary_id;
                                const name =
                                  (cloneReport.beneficiaryNames && cloneReport.beneficiaryNames[bid]) || "—";
                                const err = row.error && typeof row.error === "string" ? row.error : "";
                                const preview = truncateText(err, 200);
                                return (
                                  <tr key={bid || idx}>
                                    <td>
                                      <div className="font-weight-bold">{name}</div>
                                      {bid && (
                                        <div className="font-monospace text-muted" style={{ fontSize: "0.85rem", wordBreak: "break-all" }}>
                                          {bid}
                                        </div>
                                      )}
                                    </td>
                                    <td>{rowStatusBadge(row.status)}</td>
                                    <td style={{ fontSize: "0.98rem" }}>{row.message || "—"}</td>
                                    <td style={{ fontSize: "0.98rem" }}>
                                      {!err ? (
                                        <span className="text-muted">—</span>
                                      ) : err.length <= 200 ? (
                                        <pre
                                          className="mb-0 p-2 rounded bg-light border"
                                          style={{ whiteSpace: "pre-wrap", maxHeight: 160, overflow: "auto", fontSize: "0.9rem" }}
                                        >
                                          {err}
                                        </pre>
                                      ) : (
                                        <>
                                          <pre
                                            className="mb-1 p-2 rounded bg-light border"
                                            style={{ whiteSpace: "pre-wrap", maxHeight: 120, overflow: "auto", fontSize: "0.9rem" }}
                                          >
                                            {preview}
                                          </pre>
                                          <details style={{ fontSize: "0.95rem" }}>
                                            <summary className="text-primary" style={{ cursor: "pointer" }}>
                                              Show full error
                                            </summary>
                                            <pre
                                              className="mt-2 mb-0 p-2 rounded bg-light border"
                                              style={{ whiteSpace: "pre-wrap", maxHeight: 280, overflow: "auto", fontSize: "0.9rem" }}
                                            >
                                              {err}
                                            </pre>
                                          </details>
                                        </>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={cloneConfirmOpen}
        toggle={closeCloneModal}
        centered
        size="lg"
        backdrop={isCloning ? "static" : true}
        keyboard={!isCloning}
        style={{ fontSize: "1.0625rem" }}
      >
        <ModalHeader toggle={closeCloneModal} className={isCloning ? "border-bottom-0" : ""}>
          {isCloning ? "Cloning in progress" : "Confirm clone operation"}
        </ModalHeader>
        <ModalBody className="pt-0">
          {!isCloning ? (
            <>
              <p className="mb-4" style={{ fontSize: "1.1rem" }}>
                You are about to clone{" "}
                <strong className="text-dark">{selectedBeneficiaries.length}</strong> beneficiary
                {selectedBeneficiaries.length === 1 ? "" : "ies"} from the source below into the target component.
                This cannot be undone from this screen.
              </p>
              <div className="row">
                <div className="col-md-6 mb-3 mb-md-0">
                  {endpointSummaryCard(
                    "Source",
                    "#0d6efd",
                    previewLabels.sourceProject,
                    previewLabels.sourceComponent,
                    sourceProjectId,
                    sourceComponentId,
                    "p-4"
                  )}
                </div>
                <div className="col-md-6">
                  {endpointSummaryCard(
                    "Target",
                    "#198754",
                    previewLabels.targetProject,
                    previewLabels.targetComponent,
                    targetProjectId,
                    targetComponentId,
                    "p-4"
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-4 px-2">
              <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} type="border" />
              <p className="mt-4 mb-1 font-weight-bold" style={{ fontSize: "1.15rem" }}>
                Copying beneficiaries to the target…
              </p>
              <p className="text-muted mb-4" style={{ fontSize: "1rem" }}>
                Please keep this window open until the operation finishes.
              </p>
              <Progress animated striped value={100} color="success" className="mb-2" style={{ height: "0.65rem" }} />
              <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
                Indeterminate progress — the server is processing your request.
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter className={isCloning ? "border-top-0 justify-content-center" : ""}>
          {!isCloning ? (
            <>
              <Button color="secondary" size="lg" onClick={closeCloneModal}>
                Cancel
              </Button>
              <Button color="success" size="lg" onClick={executeClone}>
                Yes, clone now
              </Button>
            </>
          ) : (
            <span className="text-muted" style={{ fontSize: "0.95rem" }}>
              Working…
            </span>
          )}
        </ModalFooter>
      </Modal>

      <LocationPickerModal
        isOpen={isLocationPickerOpen}
        toggle={() => setIsLocationPickerOpen(false)}
        title="Select source location"
        initialValue={selectedSourceLocation}
        onSelect={handleSourceLocationSelect}
      />

      <Modal isOpen={phoneListModalOpen} toggle={() => setPhoneListModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setPhoneListModalOpen(false)}>
          Filter with phone list
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3" style={{ fontSize: "0.95rem" }}>
            Paste phone numbers copied from Excel (one per line, or separated by commas/tabs). Only
            beneficiaries matching these numbers will be shown in the table.
          </p>
          <textarea
            className="form-control"
            rows={14}
            style={{ fontSize: "1.05rem", lineHeight: 1.5, fontFamily: "monospace" }}
            placeholder="Paste phone numbers here..."
            value={phoneListInput}
            onChange={(e) => setPhoneListInput(e.target.value)}
          />
          {phoneListFilter.length > 0 && (
            <div className="text-muted mt-2" style={{ fontSize: "0.9rem" }}>
              Active filter: {phoneListFilter.length.toLocaleString()} number(s)
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={clearPhoneListFilter}>
            Clear phone list
          </Button>
          <Button color="secondary" onClick={() => setPhoneListModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={applyPhoneListFilter}>
            Filter
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CloneBeneficiaries;
